import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Eye, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { useQueryClient } from '@tanstack/react-query'
import type { Role, UserWithRoles } from '@/lib/api/user-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { CreateUserDialog } from '@/components/users/create-user-dialog'
import { ChangePasswordDialog } from '@/components/users/change-password-dialog'
import { UserActionsMenu } from '@/components/users/user-actions-menu'
import { UserOtpDialog } from '@/components/users/user-otp-dialog'
import { UserListCard } from '@/components/users/user-list-card'
import { UserRolesDialog } from '@/components/users/user-roles-dialog'
import {
    useRoles,
    useUsers,
    useUsersInfinite,
    userKeys,
} from '@/hooks/use-user-query'
import { ADMIN_ROLE_CODE, useAuth } from '@/hooks/use-auth'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { userApi } from '@/lib/api/user-api'
import { impersonationApi } from '@/lib/api'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Trước đây cả hai nằm trong `useState` nên không vào URL (R9).
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.user_search_label,
        placeholder: m.user_search_placeholder,
    }),
    role_id: remoteSelectFilter({
        label: m.user_role_filter_label,
        allLabel: m.user_all_roles,
        useOptions: () => {
            const { data } = useRoles()
            return (data ?? []).map((r: Role) => ({
                value: r.id,
                label: r.name,
            }))
        },
    }),
})

export const Route = createFileRoute('/_app/_authed/_sidebar/users/')({
    component: UsersPage,
    validateSearch: FILTERS.validateSearch,
})

function UsersPage() {
    const navigate = useNavigate({ from: '/users/' })
    const urlSearch = Route.useSearch()

    // Chip lọc theo vai trò cần TÊN, URL chỉ có id → tra runtime rồi truyền.
    const { data: roles = [] } = useRoles()
    const roleName = roles.find(
        (r: Role) => r.id === (urlSearch.role_id as string | undefined),
    )?.name

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
        chipLabels: {
            role_id: roleName
                ? `${m.user_role_filter_label()}: ${roleName}`
                : undefined,
        },
    })

    const [dialogOpen, setDialogOpen] = useState(false)

    // Mobile dùng load-more (useUsersInfinite), desktop giữ nút Trước/Sau.
    // `enabled` để đúng một nguồn dữ liệu chạy, không gọi API thừa.
    const isMobile = useIsMobile()

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data, isLoading } = useUsers(
        {
            search: values.q || undefined,
            role_id: values.role_id === 'all' ? undefined : values.role_id,
            page,
            per_page: 20,
        },
        { enabled: !isMobile },
    )

    const infiniteParams = useMemo(
        () => ({
            search: values.q || undefined,
            role_id: values.role_id === 'all' ? undefined : values.role_id,
            per_page: 20,
        }),
        [values.q, values.role_id],
    )
    const infinite = useUsersInfinite(infiniteParams, { enabled: isMobile })
    const mobileUsers = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const users = data?.data ?? []
    const meta = data?.meta

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const filtering = barProps.activeCount > 0 || !!values.q
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtering ? m.list_empty_filtered() : m.user_no_data()}
            </span>
            {filtering ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            {/* Dưới `sm` tiêu đề và nút xuống hai dòng; từ `sm` giữ nguyên hàng
                ngang như cũ. */}
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">{m.user_title()}</h1>
                </div>
                <Button size="sm" onClick={() => setDialogOpen(true)}>
                    <Plus className="size-4" />
                    {m.user_add()}
                </Button>
            </div>

            <ListFilterBar {...barProps} />

            {/* Mobile: card list + load-more — bảng 6 cột không vừa 390px */}
            {infinite.isLoading || mobileUsers.length === 0 ? (
                <div className="rounded-md border md:hidden">
                    {infinite.isLoading ? (
                        <div className="py-8 text-center text-muted-foreground">
                            {m.common_loading()}
                        </div>
                    ) : (
                        emptyState
                    )}
                </div>
            ) : (
                <div className="space-y-2 md:hidden">
                    {mobileUsers.map((u: UserWithRoles) => (
                        <UserCardItem key={u.id} user={u} roles={roles} />
                    ))}
                    {infinite.hasNextPage && (
                        <Button
                            variant="outline"
                            className="h-11 w-full"
                            disabled={infinite.isFetchingNextPage}
                            onClick={() => void infinite.fetchNextPage()}
                        >
                            {infinite.isFetchingNextPage
                                ? m.list_loading_more()
                                : m.list_load_more()}
                        </Button>
                    )}
                </div>
            )}

            {/* Desktop: table (giữ nguyên như trước) */}
            <div className="hidden rounded-md border md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.user_full_name()}</TableHead>
                            <TableHead>{m.auth_username()}</TableHead>
                            <TableHead>{m.user_role()}</TableHead>
                            <TableHead>{m.user_status()}</TableHead>
                            <TableHead>{m.user_created_date()}</TableHead>
                            <TableHead className="w-[100px]">
                                {m.common_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            users.map((u: UserWithRoles) => (
                                <UserRow key={u.id} user={u} roles={roles} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination (desktop — mobile đã có nút Xem thêm) */}
            {meta && meta.total_pages > 1 && (
                <div className="hidden items-center justify-between text-sm text-muted-foreground md:flex">
                    <span>
                        {m.user_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}

            <CreateUserDialog open={dialogOpen} onOpenChange={setDialogOpen} />
        </div>
    )
}

/** Card mobile: cùng dữ liệu với `UserRow` nhưng bố cục dọc, thao tác gom menu. */
function UserCardItem({ user, roles }: { user: UserWithRoles; roles: Role[] }) {
    const qc = useQueryClient()
    const { hasPermission, user: currentUser } = useAuth()
    const [pwDialogOpen, setPwDialogOpen] = useState(false)
    const [rolesDialogOpen, setRolesDialogOpen] = useState(false)
    const [otpDialogOpen, setOtpDialogOpen] = useState(false)
    const [impersonating, setImpersonating] = useState(false)

    const canImpersonate =
        hasPermission('users.impersonate') && currentUser?.id !== user.id
    // Backend mới là cổng thật; đây chỉ quyết định có hiện mục trong menu không.
    const canManageOtp = hasPermission('users.update')

    const handleImpersonate = async () => {
        setImpersonating(true)
        try {
            await impersonationApi.start(user.id)
            qc.clear()
            window.location.reload()
        } catch {
            setImpersonating(false)
            toast.error(m.impersonation_start_error())
        }
    }

    const toggleActive = async () => {
        try {
            await userApi.update(user.id, { is_active: !user.is_active })
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(
                user.is_active
                    ? m.user_deactivate_success()
                    : m.user_activate_success(),
            )
        } catch {
            toast.error(m.user_update_error())
        }
    }

    return (
        <>
            <UserListCard
                fullName={user.full_name}
                username={user.username}
                createdAt={user.created_at}
                isActive={user.is_active}
                assignedRoles={user.roles ?? []}
                onEditRoles={() => setRolesDialogOpen(true)}
                actions={
                    <UserActionsMenu
                        isActive={user.is_active}
                        canImpersonate={canImpersonate}
                        impersonating={impersonating}
                        onImpersonate={handleImpersonate}
                        onChangePassword={() => setPwDialogOpen(true)}
                        onManageOtp={
                            canManageOtp
                                ? () => setOtpDialogOpen(true)
                                : undefined
                        }
                        onToggleActive={toggleActive}
                    />
                }
            />
            <ChangePasswordDialog
                open={pwDialogOpen}
                onOpenChange={setPwDialogOpen}
                userId={user.id}
                userName={user.full_name}
            />
            <UserOtpDialog
                open={otpDialogOpen}
                onOpenChange={setOtpDialogOpen}
                userId={user.id}
                userName={user.full_name}
                email={user.email}
                otpEnabled={user.otp_enabled}
            />
            <UserRolesDialog
                open={rolesDialogOpen}
                onOpenChange={setRolesDialogOpen}
                userId={user.id}
                userName={user.full_name}
                roles={roles}
                assignedRoleIds={(user.roles ?? []).map((r) => r.id)}
            />
        </>
    )
}

function UserRow({ user, roles }: { user: UserWithRoles; roles: Role[] }) {
    const qc = useQueryClient()
    const { hasPermission, user: currentUser } = useAuth()
    const isSelf = currentUser?.id === user.id
    const [pwDialogOpen, setPwDialogOpen] = useState(false)
    const [otpDialogOpen, setOtpDialogOpen] = useState(false)
    const [impersonating, setImpersonating] = useState(false)

    // Backend is the real gate; this only decides whether to show the entry.
    const canImpersonate =
        hasPermission('users.impersonate') && currentUser?.id !== user.id
    const canManageOtp = hasPermission('users.update')

    const handleImpersonate = async () => {
        setImpersonating(true)
        try {
            await impersonationApi.start(user.id)
            // React Query caches are endpoint-keyed, not identity-keyed: clear
            // everything and hard-reload so the app re-renders fully as the target.
            qc.clear()
            window.location.reload()
        } catch {
            setImpersonating(false)
            toast.error(m.impersonation_start_error())
        }
    }

    const toggleActive = async () => {
        try {
            await userApi.update(user.id, { is_active: !user.is_active })
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(
                user.is_active
                    ? m.user_deactivate_success()
                    : m.user_activate_success(),
            )
        } catch {
            toast.error(m.user_update_error())
        }
    }

    const handleRoleChange = async (roleId: string) => {
        const currentRoleIds = (user.roles ?? []).map((r) => r.id)
        const newRoleIds = currentRoleIds.includes(roleId)
            ? currentRoleIds.filter((id) => id !== roleId)
            : [...currentRoleIds, roleId]
        try {
            await userApi.assignRoles(user.id, newRoleIds)
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(m.toast_user_role_success())
        } catch {
            toast.error(m.toast_user_role_error())
        }
    }

    return (
        <TableRow>
            <TableCell className="font-medium">{user.full_name}</TableCell>
            <TableCell className="text-muted-foreground">
                {user.username}
                {user.otp_enabled && (
                    <Badge variant="outline" className="ml-2">
                        {m.user_otp_badge()}
                    </Badge>
                )}
            </TableCell>
            <TableCell>
                <div className="flex flex-wrap gap-1">
                    {roles.map((role) => {
                        const hasRole = user.roles?.some(
                            (r) => r.id === role.id,
                        )
                        // Bỏ vai trò Quản trị viên của CHÍNH MÌNH là tự khoá
                        // vĩnh viễn — không còn đường nào tự cấp lại. Backend
                        // chặn ở `guardOwnAdminRole`; đây là lớp hiển thị.
                        const lockedOwnAdmin =
                            isSelf && role.code === ADMIN_ROLE_CODE && hasRole
                        return (
                            <button
                                key={role.id}
                                type="button"
                                disabled={lockedOwnAdmin}
                                onClick={() => handleRoleChange(role.id)}
                                title={
                                    lockedOwnAdmin
                                        ? m.user_roles_cannot_drop_own_admin()
                                        : hasRole
                                          ? m.user_role_remove_title({
                                                name: role.name,
                                            })
                                          : m.user_role_add_title({
                                                name: role.name,
                                            })
                                }
                                className="disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <Badge
                                    variant={hasRole ? 'default' : 'outline'}
                                    className="cursor-pointer text-xs"
                                >
                                    {role.name}
                                </Badge>
                            </button>
                        )
                    })}
                </div>
            </TableCell>
            <TableCell>
                <button type="button" onClick={toggleActive}>
                    <Badge
                        variant={user.is_active ? 'success' : 'secondary'}
                        className="cursor-pointer"
                    >
                        {user.is_active
                            ? m.customer_active()
                            : m.customer_inactive()}
                    </Badge>
                </button>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
                {new Date(user.created_at).toLocaleDateString('vi-VN')}
            </TableCell>
            <TableCell>
                {canImpersonate && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleImpersonate}
                        disabled={impersonating}
                        className="text-xs"
                    >
                        <Eye className="size-4" />
                        {m.impersonation_view_as_action()}
                    </Button>
                )}
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPwDialogOpen(true)}
                    className="text-xs"
                >
                    {m.user_change_pw_btn()}
                </Button>
                {canManageOtp && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setOtpDialogOpen(true)}
                        className="text-xs"
                    >
                        {m.user_otp_action()}
                    </Button>
                )}
                <ChangePasswordDialog
                    open={pwDialogOpen}
                    onOpenChange={setPwDialogOpen}
                    userId={user.id}
                    userName={user.full_name}
                />
                <UserOtpDialog
                    open={otpDialogOpen}
                    onOpenChange={setOtpDialogOpen}
                    userId={user.id}
                    userName={user.full_name}
                    email={user.email}
                    otpEnabled={user.otp_enabled}
                />
            </TableCell>
        </TableRow>
    )
}
